import React, { useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IJobsProps } from "../types";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { notification } from "antd";
import { formatJobYearsLabel } from "../utils/formatJobYearsLabel";
import {
  trackMetaJobLeadForListing,
  trackMetaJobsListApplyClick,
  trackMetaJobsListViewDetailsClick,
} from "utils/metaPixelJob";

interface JobCardProps {
  job: IJobsProps;
  handleJob: (id: any) => void;
  likeJob: (
    id: any,
    hasLiked: boolean,
    onSuccess?: () => void,
    onError?: () => void,
  ) => void;
  handleBookmark: (id: any, hasBookmarked: boolean) => void;
  hideApplyButton?: boolean;
  isProfileComplete?: boolean;
  incompleteSections?: string[];
}

export const JobCard: React.FC<JobCardProps> = ({
  job,
  likeJob,
  handleBookmark,
  hideApplyButton,
  isProfileComplete = true,
  incompleteSections = [],
}) => {
  const navigate = useNavigate();
  const [jobSummaryExpanded, setJobSummaryExpanded] = useState(false);
  const [jobSummaryClamped, setJobSummaryClamped] = useState(false);
  const jobSummaryRef = useRef<HTMLParagraphElement>(null);

  const hasApplied = job.has_liked;
  const isProfileIncomplete = !isProfileComplete;
  const isApplyDisabled = hasApplied || isProfileIncomplete;

  const getButtonStyles = () => {
    if (hasApplied) {
      return "bg-green-100 text-green-700 cursor-not-allowed";
    }
    if (isProfileIncomplete) {
      return "bg-blue-100 text-blue-600 cursor-not-allowed";
    }
    return "bg-[#00094A] text-white hover:bg-[#00094A]/90 active:scale-95";
  };

  const getButtonText = () => {
    if (hasApplied) return "Applied";
    if (isProfileIncomplete) return "Complete your Profile to Apply";
    return "Apply Now";
  };

  const getButtonTitle = () => {
    if (hasApplied) return "You have already applied for this job";
    if (isProfileIncomplete)
      return `Complete your profile to apply (Missing: ${incompleteSections.join(", ")})`;
    return "Click to apply";
  };

  useLayoutEffect(() => {
    if (!job.jobsummary) {
      setJobSummaryClamped(false);
      return;
    }
    const el = jobSummaryRef.current;
    if (!el) return;

    if (jobSummaryExpanded) {
      setJobSummaryClamped(true);
      return;
    }

    const measure = () => {
      setJobSummaryClamped(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    requestAnimationFrame(measure);
  }, [job.jobsummary, job.id, jobSummaryExpanded]);

  const getInitials = (name?: string) => {
    if (!name) return "NA";
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  };

  const formatEmploymentType = (type: string) =>
    type.replace("-", " ").replace(/\b\w/g, (l) => l.toUpperCase());

  dayjs.extend(relativeTime);

  const skills = [
    job.level?.replace("_", " "),
    formatEmploymentType(job.employment_type),
    job.years_of_experience
      ? formatJobYearsLabel(job.years_of_experience).replace(
          / years$/,
          " yrs exp",
        )
      : null,
  ].filter(Boolean);

  const handleBookmarkClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isProfileComplete) {
      notification.error({
        message: "Profile Incomplete",
        description: `Please complete your profile before saving jobs. Missing: ${incompleteSections.join(", ")}`,
        duration: 4,
      });
      return;
    }

    if (job.id !== undefined) {
      handleBookmark(job.id, !job.has_bookmarked);
    }
  };

  const handleApplyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isApplyDisabled) {
      if (isProfileIncomplete) {
        notification.error({
          message: "Profile Incomplete",
          description: `Please complete your profile before applying for jobs. Missing: ${incompleteSections.join(", ")}`,
          duration: 5,
        });
      }
      return;
    }

    trackMetaJobsListApplyClick({
      id: job.id,
      slug: job.slug,
      title: job.title,
    });

    if (!job.has_liked && job.id !== undefined) {
      likeJob(
        job.id,
        true,
        () => {
          trackMetaJobLeadForListing({
            id: job.id,
            slug: job.slug,
            title: job.title,
          });
          notification.success({
            message: "Application Successful!",
            description: `You have successfully applied for "${job.title}" at ${job.company_name}.`,
            duration: 4,
          });
        },
        () => {
          notification.error({
            message: "Application Failed",
            description:
              "There was an error submitting your application. Please try again.",
            duration: 4,
          });
        },
      );
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm relative w-full">
      <div className="absolute right-6 top-6">
        <button
          onClick={handleBookmarkClick}
          className="p-2"
          style={{
            opacity: isProfileComplete ? 1 : 0.5,
            cursor: isProfileComplete ? "pointer" : "not-allowed",
          }}
          title={
            !isProfileComplete
              ? `Complete profile to bookmark (Missing: ${incompleteSections.join(", ")})`
              : job.has_bookmarked
                ? "Bookmarked"
                : "Bookmark Job"
          }
          disabled={!isProfileComplete}
        >
          <i
            className={`ri-bookmark-${
              job.has_bookmarked ? "fill" : "line"
            } text-xl`}
          />
        </button>
      </div>

      <div className="flex gap-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white font-semibold">
          {getInitials(job.company_name)}
        </div>

        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-semibold text-lg text-gray-900">{job.title}</h2>

            {job.status !== "draft" && !job.expired && (
              <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-medium">
                Open
              </span>
            )}
          </div>
          <p className="text-gray-600 mt-1">{job.company_name}</p>

          <div className="flex flex-wrap gap-6 text-gray-500 text-sm mt-3">
            <span className="flex items-center gap-1">
              <i className="ri-map-pin-line" />
              {job.location_city}, {job.location_country}
            </span>

            <span className="flex items-center gap-1">
              <i className="ri-briefcase-2-line" />
              {formatEmploymentType(job.employment_type)}
            </span>

            <span className="flex items-center gap-1">
              <i className="ri-time-line" />
              {dayjs(job.created_at).fromNow()}
            </span>
          </div>

          {job.jobsummary && (
            <div className="mt-4">
              <p
                ref={jobSummaryRef}
                className={`text-gray-600 text-sm${
                  jobSummaryExpanded ? "" : " line-clamp-4"
                }`}
              >
                <span className="font-bold text-gray-800">JOB SUMMARY: </span>
                {job.jobsummary}
              </p>
              {jobSummaryClamped && (
                <button
                  type="button"
                  onClick={() => setJobSummaryExpanded((v) => !v)}
                  className="mt-1 text-sm font-medium text-[#00094A] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00094A]/30 rounded"
                >
                  {jobSummaryExpanded ? "Show less" : "Show more"}
                </button>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-2 mt-4">
            {skills.map((skill) => (
              <span
                key={skill}
                className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs"
              >
                {skill}
              </span>
            ))}
          </div>

          <div className="flex gap-4 mt-6">
            {!hideApplyButton && (
              <button
                type="button"
                disabled={isApplyDisabled}
                onClick={handleApplyClick}
                className={`px-5 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${getButtonStyles()}`}
                title={getButtonTitle()}
              >
                {getButtonText()}
              </button>
            )}

            <button
              type="button"
              className="border border-[#00094A] text-[#00094A] px-5 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-all duration-200"
              onClick={() => {
                trackMetaJobsListViewDetailsClick({
                  id: job.id,
                  slug: job.slug,
                  title: job.title,
                });
                navigate(`/jobs/${job.slug}`);
              }}
            >
              View Details
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
